import { tokenize, type MentionableMember } from '@/lib/linkify'
import { cn } from '@/lib/utils'

/**
 * Plain text, with the parts that are not really plain rendered as such.
 *
 * Comments, blocked and unblock notes and anything else somebody typed as text. Rich-text
 * fields do not come through here — the API turns bare URLs into anchors on write, so a
 * description arrives with the markup already in it and `RichText` renders that.
 *
 * Notably this does NOT build a string of HTML. `RichText` is the only module allowed to
 * call `dangerouslySetInnerHTML`, and that concentration is what makes rendering stored
 * markup reviewable; producing markup here would either break the rule or need a second
 * sanitizer to be safe. React escapes the text nodes, correctly and for free, and an `href`
 * built from an allow-list of two schemes cannot carry script.
 */
export function LinkedText({
  text,
  members = [],
  className,
}: {
  text: string
  /** Whose names light up as mentions. Omit for pure linkifying. */
  members?: readonly MentionableMember[]
  className?: string
}) {
  const tokens = tokenize(text, members)

  return (
    // `whitespace-pre-wrap`, because the line breaks somebody typed are part of what they
    // wrote — this replaced a plain <p> that already had it, and losing it would reflow
    // every existing comment.
    <span className={cn('whitespace-pre-wrap', className)}>
      {tokens.map((token, i) => {
        if (token.kind === 'text') return <span key={i}>{token.value}</span>

        if (token.kind === 'mention') {
          return (
            <span
              key={i}
              className="rounded-[4px] bg-brand-soft px-1 font-medium text-brand-dark"
              // Not a link. There is a person page, but a mention is about who was addressed
              // rather than an invitation to go and read their profile, and making every name
              // in every comment a navigation target is how a thread becomes a minefield.
            >
              {token.value}
            </span>
          )
        }

        return (
          <a
            key={i}
            href={token.href}
            // The same hardening the API applies to links in rich text: a new tab, and rel
            // closing the reverse-tabnabbing hole that target="_blank" opens.
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand underline decoration-brand/40 underline-offset-2 hover:decoration-brand"
          >
            {token.value}
          </a>
        )
      })}
    </span>
  )
}
